/**
 * Export adapter -- provenance tagging on chart image export.
 *
 * When a chart is exported as PNG, this adapter applies a visible provenance
 * tag strip and embeds invisible metadata so the image carries its lineage.
 *
 * Supports PNG (visual tag + tEXt metadata), SVG (metadata element + data
 * attributes), and HTML (meta tags + footer div).
 */

import { createTaggedImage, type TagMode } from "../provenance/ProvenanceTag";
import {
  embedPngMetadata,
  embedSvgMetadata,
  embedHtmlMetadata,
  buildAssetUrl,
  type ThreadMetadata,
} from "../../lib/threadMetadata";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ExportOptions {
  chartId: string;
  artifactIds: string[];
  imageData: Blob;
}

export interface ExportWithProvenanceOptions {
  imageBlob: Blob;
  artifactId: string;
  projectId: string;
  tagMode?: TagMode;
  format?: "png" | "svg" | "html";
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildMeta(artifactId: string, projectId: string): ThreadMetadata {
  return {
    version: "1.0",
    assetUrl: buildAssetUrl(artifactId),
    projectId,
    artifactId,
    createdAt: new Date().toISOString(),
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Apply provenance tagging to an exported image.
 *
 * For PNG: adds a visible tag strip below the plot + embeds tEXt metadata.
 * For SVG: embeds metadata element and data attributes on the root SVG.
 * For HTML: inserts meta tags in <head> and a footer div.
 *
 * Returns the tagged Blob ready for download.
 */
export async function exportWithProvenance(
  options: ExportWithProvenanceOptions,
): Promise<Blob> {
  const {
    imageBlob,
    artifactId,
    projectId,
    tagMode = "qr",
    format = "png",
  } = options;

  if (tagMode === "none" && format === "png") {
    // Still embed invisible metadata even when visual tag is disabled
    const meta = buildMeta(artifactId, projectId);
    return embedPngMetadata(imageBlob, meta);
  }

  switch (format) {
    case "png":
      return createTaggedImage(imageBlob, artifactId, projectId, tagMode);

    case "svg": {
      const svgText = await imageBlob.text();
      const meta = buildMeta(artifactId, projectId);
      const tagged = embedSvgMetadata(svgText, meta);
      return new Blob([tagged], { type: "image/svg+xml" });
    }

    case "html": {
      const htmlText = await imageBlob.text();
      const meta = buildMeta(artifactId, projectId);
      const tagged = embedHtmlMetadata(htmlText, meta);
      return new Blob([tagged], { type: "text/html" });
    }

    default:
      return imageBlob;
  }
}

/**
 * Legacy API -- wraps exportWithProvenance for the original ExportOptions
 * interface used by AG Charts integration.
 */
export async function exportWithARQO(options: ExportOptions): Promise<Blob> {
  const artifactId = options.artifactIds[0];
  if (!artifactId) return options.imageData;

  return exportWithProvenance({
    imageBlob: options.imageData,
    artifactId,
    projectId: options.chartId,
    tagMode: "qr",
    format: "png",
  });
}

/**
 * Trigger a file download in the browser.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
